import { createInstance } from "localforage";

export default class DB {
    /**
     * 创建实例, DB名称默认为'iWMS_DB'
     * @param {string} name 
     */
    #dbInstance;
    constructor(name = 'iWMS_DB') {
        this.#dbInstance = createInstance({ name });;
    }
    // 获取数据
    async getItem(key) {
        try {
            return await this.#dbInstance.getItem(key);
        } catch (err) {
            console.error(err);
        }
    };
    // 设置数据
    async setItem(key, value) {
        try {
            await this.#dbInstance.setItem(key, value);
        } catch (err) {
            console.error(err);
        }
    };
    // 删除数据
    async removeItem(key) {
        try {
            await this.#dbInstance.removeItem(key);
        } catch (err) {
            console.error(err);
        }
    };
    // 清空数据
    async clear() {
        try {
            await this.#dbInstance.clear();
        } catch (err) {
            console.error(err);
        }
    };
    // 获取所有key
    async keys() {
        try {
            const keys = await this.#dbInstance.keys();
            return keys;
        } catch (err) {
            console.error(err);
        }
    };
    /**
     * 根据键数组获取数据
     * @param {String | Number} id 一般为单号或容器号
     * @param {Array} keys 存储的键数组
     * @returns {Promise}
     */
    getByKeys(id, keys) {
        if (!['string', 'number'].includes(typeof id)) {
            console.error(`id【${id}】is not a string or number`);
            return;
        }
        if (!Array.isArray(keys)) {
            console.error('keys must be a array');
            return;
        }
        return Promise.all(keys.map(async key => {
            if (!['string', 'number'].includes(typeof key)) {
                console.error(`key【${key}】is not a string or number`);
                return;
            }
            return await this.getItem(`${id}__${key}`);
        }));
    }
    /**
     * 根据单个键获取数据
     * @param {String | Number} id 一般为单号或容器号
     * @param {String | Number} key 存储的键
     * @returns {Promise}
     */
    getByKey(id, key) {
        if (!['string', 'number'].includes(typeof id)) {
            console.error(`id【${id}】is not a string or number`);
            return;
        }
        if (!['string', 'number'].includes(typeof key)) {
            console.error(`key【${key}】is not a string or number`);
            return;
        }
        return this.getItem(`${id}__${key}`);
    }
    /**
     * 根据id获取数据
     * @param {String | Number} id 一般为单号或容器号
     * @returns {Promise}
     */
    async getById(id) {
        if (!['string', 'number'].includes(typeof id)) {
            console.error(`id【${id}】is not a string or number`);
            return;
        }
        const keys = await this.keys();
        const thisIdKeys = keys.filter(key => String(key).includes(id));
        const res = {};
        for (const key of thisIdKeys) {
            const value = await this.getItem(key);
            const keyArr = key.split('__');
            const keyName = keyArr[keyArr.length - 1];
            if(keyName === 'otherData'){
                Object.assign(res, value)
            } else {
                res[keyName] = value;
            }
        }
        return res;
    }
    /**
     * 根据键数组存储数据到indexedDB, 其中传入的keys会以每个key单独存储，其余数据存储到otherData中
     * @param {Object} data 待存储的数据
     * @param {String | Number} id 一般为单号或容器号
     * @param {Array} keys 存储的键数组
     * @returns {Promise}
     */
    setByKeys(data, id, keys) {
        if (Object.prototype.toString.call(data) !== '[object Object]') {
            console.error('data must be a object');
            return;
        }
        if (!['string', 'number'].includes(typeof id)) {
            console.error(`id【${id}】is not a string or number`);
            return;
        }
        if (!Array.isArray(keys)) {
            console.error('keys must be a array');
            return;
        }
        const otherKeys = Object.keys(data).filter(key => !keys.includes(key));
        if (otherKeys.length > 0) {
            const otherData = {};
            for (const key of otherKeys) {
                otherData[key] = data[key];
            }
            this.setItem(`${id}__otherData`, otherData);
        }
        return Promise.all(keys.map(key => {
            if (!['string', 'number'].includes(typeof key)) {
                console.error(`key【${key}】is not a string or number`);
                return;
            }
            if (!data.hasOwnProperty(key)) {
                console.error(`key【${key}】is not a property of data`);
                return;
            }
            return this.setItem(`${id}__${key}`, data[key]);
        }));
    }
    /**
     * 根据单个键存储数据到indexedDB
     * @param {*} data 待存储的数据
     * @param {String | Number} id 一般为单号或容器号
     * @param {String | Number} key 存储的键
     * @returns {Promise}
     */
    setByKey(data, id, key) {
        if (Object.prototype.toString.call(data) !== '[object Object]') {
            console.error('data must be a object');
            return;
        }
        if (!['string', 'number'].includes(typeof id)) {
            console.error(`id【${id}】is not a string or number`);
            return;
        }
        if (!['string', 'number'].includes(typeof key)) {
            console.error(`key【${key}】is not a string or number`);
            return;
        }
        if (!data.hasOwnProperty(key)) {
            console.error(`key【${key}】is not a property of data`);
            return;
        }
        return this.setItem(`${id}__${key}`, data);
    }
    /**
     * 根据键数组删除数据
     * @param {String | Number} id 一般为单号或容器号
     * @param {Array} keys 删除的键数组
     * @returns {Promise}
     */
    removeByKeys(id, keys) {
        if (!['string', 'number'].includes(typeof id)) {
            console.error(`id【${id}】is not a string or number`);
            return;
        }
        if (!Array.isArray(keys)) {
            console.error('keys must be a array');
            return;
        }
        return Promise.all(keys.map(key => {
            if (!['string', 'number'].includes(typeof key)) {
                console.error(`key【${key}】is not a string or number`);
                return;
            }
            return this.removeItem(`${id}__${key}`);
        }));
    }
    /**
     * 根据单个键删除数据
     * @param {String | Number} id 一般为单号或容器号
     * @param {String | Number} key 删除的键
     * @returns {Promise}
     */
    removeByKey(id, key) {
        if (!['string', 'number'].includes(typeof id)) {
            console.error(`id【${id}】is not a string or number`);
            return;
        }
        if (!['string', 'number'].includes(typeof key)) {
            console.error(`key【${key}】is not a string or number`);
            return;
        }
        return this.removeItem(`${id}__${key}`);
    }
    /**
     * 根据id删除数据
     * @param {String | Number} id 一般为单号或容器号
     * @returns {Promise}
     */
    async removeById(id) {
        if (!['string', 'number'].includes(typeof id)) {
            console.error(`id【${id}】is not a string or number`);
            return;
        }
        const keys = await this.keys();
        const thisIdKeys = keys.filter(key => String(key).includes(id));
        return Promise.all(thisIdKeys.map(key => this.removeItem(key)));
    }
}

window.__DB = DB;